<script setup lang="ts" generic="Value extends PickerValue = Dayjs">
import { useControlId } from '../../composables/useControlId'
import { computed, nextTick, ref, shallowRef, watch } from 'vue'
import dayjs, { type Dayjs } from 'dayjs'
import { Calendar, XCircle } from '@lucide/vue'
import CalendarPanel from './CalendarPanel.vue'
import RenderContent from '../internal/RenderContent'
import { useBoundValue } from '../../composables/useBoundValue'
import { useFormControl } from '../../composables/useFormControl'
import { usePopup } from '../../composables/usePopup'
import { displayPickerValue, parsePickerInput, readPickerValue, writePickerValue, type PickerValue } from './dateValue'
import type { RangePickerProps } from './types'
import './picker.css'
export type { DateRangePreset } from './dateValue'

const props = withDefaults(defineProps<RangePickerProps<Value>>(), { disabled: undefined, inputReadOnly: false, allowClear: true, open: undefined, defaultOpen: false, placement: 'bottomLeft', format: 'YYYY-MM-DD', presets: () => [], ranges: () => ({}), separator: '→' })
const emit = defineEmits<{
  'update:value': [value: [Value, Value] | null]
  'update:open': [open: boolean]
  change: [value: [Value, Value] | null, dateStrings: [string, string]]
  calendarChange: [value: [Value | null, Value | null], dateStrings: [string, string], info: { range: 'start' | 'end' }]
  openChange: [open: boolean]
  focus: [event: FocusEvent]
  blur: [event: FocusEvent]
}>()
defineOptions({ inheritAttrs: false })
const { attrs, config, disabled, size, status, controlAttrs, onFieldChange, onFieldBlur } = useFormControl(props)
const innerValue = shallowRef<[Value, Value] | null>(props.defaultValue ?? null)
const value = useBoundValue(props, 'value', innerValue)
const selected = computed<[Dayjs | null, Dayjs | null]>(() => [readPickerValue(value.value?.[0], props.valueFormat), readPickerValue(value.value?.[1], props.valueFormat)])
const innerOpen = ref(props.defaultOpen)
const boundOpen = useBoundValue(props, 'open', innerOpen)
const isOpen = computed(() => !disabled.value && Boolean(boundOpen.value))
const wrapperRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)
const startRef = ref<HTMLInputElement | null>(null)
const endRef = ref<HTMLInputElement | null>(null)
const calendarRef = ref<{ focus: () => void } | null>(null)
const popupId = `rr-range-picker-${useControlId()}`
const activePart = ref<0 | 1>(0)
const draft = shallowRef<[Dayjs | null, Dayjs | null]>([...selected.value])
const hoverDate = shallowRef<Dayjs | null>(null)
const viewDate = shallowRef((selected.value[0] ?? props.defaultPickerValue?.[0] ?? dayjs()).startOf('month'))
const texts = ref<[string, string]>(['', ''])
const invalid = ref(false)
const placeholders = computed(() => props.placeholder ?? config.value.locale?.DatePicker?.lang?.rangePlaceholder ?? ['开始日期', '结束日期'])
const allPresets = computed(() => [...props.presets, ...Object.entries(props.ranges).map(([label, value]) => ({ label, value }))])
function formatDates(dates: [Dayjs | null, Dayjs | null]): [string, string] { return [displayPickerValue(dates[0], props.format), displayPickerValue(dates[1], props.format)] }
function serialize(dates: [Dayjs | null, Dayjs | null]): [Value | null, Value | null] { return [dates[0] ? writePickerValue(dates[0], props.valueFormat) as Value : null, dates[1] ? writePickerValue(dates[1], props.valueFormat) as Value : null] }
function resetDraft() {
  draft.value = [...selected.value]
  texts.value = formatDates(selected.value)
  hoverDate.value = null
  invalid.value = false
  startRef.value?.setCustomValidity('')
  endRef.value?.setCustomValidity('')
  viewDate.value = (selected.value[0] ?? props.defaultPickerValue?.[0] ?? dayjs()).startOf('month')
}
watch(value, resetDraft, { immediate: true })
watch(isOpen, open => { if (open || !invalid.value) resetDraft() })
function setOpen(open: boolean) {
  if ((disabled.value && open) || open === Boolean(boundOpen.value)) return
  innerOpen.value = open
  emit('update:open', open)
  emit('openChange', open)
}
const { container, style: popupPosition } = usePopup(isOpen, wrapperRef, panelRef, { placement: () => props.placement, getPopupContainer: () => props.getPopupContainer, close: () => setOpen(false), onEscape: () => (activePart.value ? endRef.value : startRef.value)?.focus({ preventScroll: true }) })
function publish(dates: [Dayjs, Dayjs] | null) {
  const sorted: [Dayjs, Dayjs] | null = dates ? dates[0].isAfter(dates[1]) ? [dates[1], dates[0]] : dates : null
  const next = sorted ? serialize(sorted) as [Value, Value] : null
  innerValue.value = next
  texts.value = formatDates(sorted ?? [null, null])
  invalid.value = false
  startRef.value?.setCustomValidity('')
  endRef.value?.setCustomValidity('')
  emit('update:value', next)
  emit('change', next, texts.value)
  return onFieldChange()
}
function select(date: Dayjs) {
  if (disabled.value || props.disabledDate?.(date)) return
  if (activePart.value === 0 || !draft.value[0]) {
    draft.value = [date, null]
    activePart.value = 1
    texts.value = formatDates(draft.value)
    emit('calendarChange', serialize(draft.value), texts.value, { range: 'start' })
    endRef.value?.focus({ preventScroll: true })
  } else {
    const dates: [Dayjs, Dayjs] = draft.value[0].isAfter(date) ? [date, draft.value[0]] : [draft.value[0], date]
    emit('calendarChange', serialize(dates), formatDates(dates), { range: 'end' })
    publish(dates)
    setOpen(false)
    endRef.value?.focus({ preventScroll: true })
  }
}
function selectPreset(preset: { value: [Dayjs, Dayjs] | (() => [Dayjs, Dayjs]) }) {
  const dates = typeof preset.value === 'function' ? preset.value() : preset.value
  if (disabled.value || dates.some(date => props.disabledDate?.(date))) return
  publish(dates)
  setOpen(false)
  startRef.value?.focus({ preventScroll: true })
}
function openAt(part: 0 | 1) { activePart.value = part; setOpen(true) }
function clear(event: MouseEvent) { event.stopPropagation(); if (!disabled.value) { publish(null); setOpen(false); startRef.value?.focus() } }
function commitInputs() {
  if (disabled.value || props.inputReadOnly) return false
  const current = formatDates(selected.value)
  if (texts.value[0] === current[0] && texts.value[1] === current[1]) return true
  if (!texts.value[0].trim() && !texts.value[1].trim() && props.allowClear) { publish(null); return true }
  const parsed = texts.value.map(text => parsePickerInput(text, props.format))
  if (!parsed[0] || !parsed[1] || parsed.some(date => date && props.disabledDate?.(date))) {
    invalid.value = true
    startRef.value?.setCustomValidity('请输入完整且有效的日期范围')
    endRef.value?.setCustomValidity('请输入完整且有效的日期范围')
    return false
  }
  publish([parsed[0], parsed[1]])
  return true
}
async function keydown(event: KeyboardEvent, part: 0 | 1) {
  if (event.isComposing || disabled.value) return
  if (event.key === 'ArrowDown') { event.preventDefault(); openAt(part); await nextTick(); calendarRef.value?.focus() }
  else if (event.key === 'Enter') { event.preventDefault(); if (commitInputs()) setOpen(false) }
}
function blur(event: FocusEvent) {
  const target = event.relatedTarget as Node | null
  if (wrapperRef.value?.contains(target) || panelRef.value?.contains(target)) return
  commitInputs()
  emit('blur', event)
  return onFieldBlur()
}
function endAttrs() { const { id: _id, ...nativeAttrs } = controlAttrs(); return nativeAttrs }
defineExpose({ focus: (options?: FocusOptions) => startRef.value?.focus(options), blur: () => { startRef.value?.blur(); endRef.value?.blur() } })
</script>

<template>
  <div ref="wrapperRef" class="rr-picker rr-picker-range" :class="[attrs.class, `rr-picker--${size}`, { 'is-open': isOpen, 'is-disabled': disabled, 'is-error': status === 'error' || invalid, 'is-warning': status === 'warning' }]" :style="attrs.style as any" @focusout="blur">
    <slot name="suffixIcon"><Calendar :size="14" class="rr-picker-icon" aria-hidden="true" /></slot>
    <input ref="startRef" v-bind="controlAttrs()" v-model="texts[0]" class="rr-picker-input" type="text" :disabled="disabled" :readonly="inputReadOnly" :placeholder="placeholders[0]" role="combobox" :aria-expanded="isOpen" :aria-controls="popupId" aria-haspopup="dialog" :aria-label="placeholders[0]" :aria-invalid="invalid || status === 'error' || undefined" @click="openAt(0)" @keydown="keydown($event, 0)" @focus="emit('focus', $event)" />
    <span class="rr-picker-icon" aria-hidden="true"><slot name="separator">{{ separator }}</slot></span>
    <input ref="endRef" v-bind="endAttrs()" v-model="texts[1]" class="rr-picker-input" type="text" :disabled="disabled" :readonly="inputReadOnly" :placeholder="placeholders[1]" role="combobox" :aria-expanded="isOpen" :aria-controls="popupId" aria-haspopup="dialog" :aria-label="placeholders[1]" :aria-invalid="invalid || status === 'error' || undefined" @click="openAt(1)" @keydown="keydown($event, 1)" @focus="emit('focus', $event)" />
    <button v-if="allowClear && value && !disabled" type="button" class="rr-picker-clear" aria-label="清除日期范围" @click="clear"><XCircle :size="14" aria-hidden="true" /></button>
  </div>
  <Teleport v-if="container" :to="container"><Transition name="rr-picker"><div v-if="isOpen" :id="popupId" ref="panelRef" class="rr-picker-popup rr-picker-range-popup" :class="popupClassName" :style="[popupStyle, popupPosition]" role="dialog" aria-label="选择日期范围" tabindex="-1" @mouseleave="hoverDate = null" @focusout="blur">
    <div class="rr-picker-panels">
      <CalendarPanel ref="calendarRef" v-model:view-date="viewDate" :range="draft" :hover-value="hoverDate" :disabled-date="disabledDate" :show-next="false" @select="select" @hover="hoverDate = $event"><template v-if="$slots.dateRender" #dateRender="slotProps"><slot name="dateRender" v-bind="slotProps" /></template></CalendarPanel>
      <CalendarPanel :view-date="viewDate.add(1, 'month')" :range="draft" :hover-value="hoverDate" :disabled-date="disabledDate" :show-prev="false" @update:view-date="viewDate = $event.subtract(1, 'month')" @select="select" @hover="hoverDate = $event"><template v-if="$slots.dateRender" #dateRender="slotProps"><slot name="dateRender" v-bind="slotProps" /></template></CalendarPanel>
    </div>
    <div v-if="allPresets.length" class="rr-picker-presets"><button v-for="(preset, index) in allPresets" :key="index" type="button" @click="selectPreset(preset)"><RenderContent :value="preset.label" /></button></div>
    <div v-if="$slots.renderExtraFooter" class="rr-picker-footer"><slot name="renderExtraFooter" /></div>
  </div></Transition></Teleport>
</template>
